import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsUpDownIcon,
} from "lucide-react";
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker as AriaDatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Label,
  Popover,
  I18nProvider,
} from "react-aria-components";
import type { ButtonProps, PopoverProps } from "react-aria-components";
import { formControlVariants, FormControlVariantsProps } from "../classes";
import { cn } from "../../../utils/cn";
import { DateValue, parseDate } from "@internationalized/date";

export * from "./utils";

export type DatePickerProps = {
  className?: string;
  label?: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  isDateUnavailable?: (date: DateValue) => boolean;
} & FormControlVariantsProps;

export function DatePicker({
  className,
  label,
  required,
  vColor,
  vSize,
  value,
  onChange,
  isDateUnavailable,
}: DatePickerProps) {
  const css = formControlVariants({ vSize, vColor });
  return (
    <I18nProvider locale="en-au">
      <AriaDatePicker
        value={value ? parseDate(value) : null}
        onChange={(x) => onChange?.(x?.toString() || "")}
        isDateUnavailable={isDateUnavailable}
        isInvalid={false}
        className="group flex flex-col gap-1"
      >
        {label ? (
          <Label className="cursor-default">
            {label}
            {required ? (
              <span className="relative -top-1 ml-[0.1rem] text-orange-600">
                *
              </span>
            ) : null}
          </Label>
        ) : null}
        <Group
          className={cn(
            "flex transition group-open:bg-white",
            css,
            "p-0 pl-3",
            className,
          )}
        >
          <DateInput className="flex flex-1 items-center pr-3">
            {(segment) => (
              <DateSegment
                segment={segment}
                className="h-fit rounded-xs px-0.5 tabular-nums caret-transparent outline-hidden placeholder-shown:italic focus:bg-gray-500 focus:text-white"
              />
            )}
          </DateInput>
          <Button
            className={cn(
              "pressed:bg-gray-100 flex items-center rounded-r-lg border-0 border-l border-solid border-l-gray-200 outline-hidden",
              "bg-transparent px-3 text-gray-700 ring-black transition focus-visible:ring-2",
            )}
          >
            <ChevronsUpDownIcon />
          </Button>
        </Group>
        <MyPopover>
          <Dialog className="p-3 text-gray-600 shadow-lg">
            <Calendar>
              <header className="flex w-full items-center gap-1 px-1 pb-3 font-serif">
                <Heading className="ml-2 flex-1 text-xl font-semibold" />
                <RoundButton slot="previous">
                  <ChevronLeftIcon />
                </RoundButton>
                <RoundButton slot="next">
                  <ChevronRightIcon />
                </RoundButton>
              </header>
              <CalendarGrid className="border-separate border-spacing-1">
                <CalendarGridHeader>
                  {(day) => (
                    <CalendarHeaderCell className="text-xs font-semibold text-gray-500">
                      {day}
                    </CalendarHeaderCell>
                  )}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => (
                    <CalendarCell
                      date={date}
                      className={cn(
                        "pressed:bg-gray-200 selected:bg-gray-700 selected:text-white outline-hidden focus-visible:ring",
                        "outside-month:text-gray-300 flex h-9 w-9 cursor-default items-center justify-center",
                        "rounded-full ring-gray-600/70 ring-offset-2 hover:bg-gray-100",
                        "data-[unavailable=true]:text-gray-300",
                        "data-[unavailable=true]:hover:bg-transparent",
                      )}
                    />
                  )}
                </CalendarGridBody>
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </MyPopover>
      </AriaDatePicker>
    </I18nProvider>
  );
}

function RoundButton(props: ButtonProps) {
  return (
    <Button
      {...props}
      className={cn(
        "pressed:bg-gray-200 flex h-9 w-9 outline-hidden focus-visible:ring-3",
        "cursor-default items-center justify-center rounded-full border-0 bg-transparent",
        "text-gray-600 ring-gray-600/70 ring-offset-2 hover:bg-gray-100",
      )}
    />
  );
}

function MyPopover(props: PopoverProps) {
  return (
    <Popover
      {...props}
      className={({ isEntering, isExiting }) =>
        `overflow-auto rounded-lg bg-white ring-1 ring-black/10 drop-shadow-lg ${
          isEntering
            ? "animate-in fade-in placement-bottom:slide-in-from-top-1 placement-top:slide-in-from-bottom-1 duration-200 ease-out"
            : ""
        } ${
          isExiting
            ? "animate-out fade-out placement-bottom:slide-out-to-top-1 placement-top:slide-out-to-bottom-1 duration-150 ease-in"
            : ""
        } `
      }
    />
  );
}
